import { ColumnLayout } from "@mittwald/flow-components/dist/components/ColumnLayout";
import { Wizard } from "@mittwald/flow-components/dist/components/Wizard";
import useWizardState from "@mittwald/flow-components/dist/components/Wizard/hooks/useWizardState";
import { useAnimationController } from "@mittwald/flow-components/dist/hooks/useAnimationController";
import useSelector from "@mittwald/flow-lib/dist/hooks/useSelector";
import React, { useEffect } from "react";
import { useForm } from "@mittwald/flow-components/dist/components/Form/hooks/useForm";
import z from "zod";
import { mittwaldApi } from "../../../api/Mittwald";
import { autorunAppInitialization } from "../../../autorun/autorunAppInitialization";
import { User } from "../../../model/user/User";
import sessionStore from "../../../store/session";
import { ContentBox } from "../../components/styled";
import { AdditionalServicesStep } from "./components/steps/AdditionalServicesStep/AdditionalServicesStep";
import { DeviceCookieStep } from "./components/steps/DeviceCookieStep/DeviceCookieStep";
import { MfaStep } from "./components/steps/MfaStep/MfaStep";
import { withValidatedProps } from "../../withValidatedProps";
import { OverviewBox } from "./components/OverviewBox/OverviewBox";
import { ResultBox } from "./components/ResultBox/ResultBox";
import { ContactStep } from "./components/steps/ContactStep/ContactStep";
import { LoginStep } from "./components/steps/LoginStep/LoginStep";
import { OverviewStep } from "./components/steps/OverviewStep/OverviewStep";
import { DetailsStep } from "./components/steps/DetailsStep/DetailsStep";
import { TariffStep } from "./components/steps/TariffStep/TariffStep";
import { TargetStep } from "./components/steps/TargetStep/TargetStep";
import { relocationRequestUrlParam } from "./relocationRequestUrlParam";
import { relocationWidgetStore } from "./store/RelocationWidgetStore";
import { LegacySystemAPI } from "./utils/LegacySystemAPI";
import type { RelocationApiData } from "../../../model/relocation/Relocation";
import { Relocation } from "../../../model/relocation/Relocation";
import {
  relocationFormDefaultValues,
  updateInformationByUser,
} from "./utils/RelocationUtils";

autorunAppInitialization();

export type RelocationReferrer = "wizard" | "widget";

export interface RelocationFormData extends RelocationApiData {
  target: {
    organisationUUID?: string;
  } & RelocationApiData["target"];
  domainTransfer: "domainTransfer" | "noDomainTransfer";
}

export default withValidatedProps(z.any(), () => {
  const { article } = relocationRequestUrlParam.decodeFromParam("p");

  const wizardState = useWizardState();
  const [success, setSuccess] = React.useState<boolean | undefined>(undefined);
  const rejectionAnimation = useAnimationController();

  const relocationForm = useForm<RelocationFormData>({
    defaultValues: relocationFormDefaultValues(undefined, article),
    onSubmit: async (values) => {
      if (
        values.target.organisationUUID === undefined ||
        values.target.organisationUUID === ""
      ) {
        console.error("Field organisationUUID is missing. Try again");
        return Promise.reject("Field organisationUUID is missing try again");
      }

      if (values.userId === undefined) {
        console.error("Field userId is missing try again");
        return Promise.reject("Field userId is missing try again");
      }

      const updatedValues = {
        ...values,
        target: {
          ...values.target,
          organisation: values.target.organisationUUID,
        },
      };
      await Relocation.create(updatedValues);
      setSuccess(true);
    },
  });

  const [loginFormError, setLoginFormError] = React.useState<string | null>(
    null,
  );
  const isNewSystem = relocationForm.watch("target.system") === "mstudio";

  const loginForm = useForm({
    defaultValues: {
      identifier: "",
      password: "",
    },
    onSubmit: async ({ identifier, password }) => {
      if (isNewSystem) {
        const response = await mittwaldApi.userAuthenticate.request({
          requestBody: {
            email: identifier,
            password,
          },
        });

        if (response.status === 200) {
          relocationWidgetStore.updateAccessToken(response.content.token);
          sessionStore.setAuthentication(response.content);

          const user = await User.me();
          updateInformationByUser(relocationForm, user);
          relocationForm.setValue("userId", user.id);
          wizardState.gotoStep("target");
        } else if (response.status === 202) {
          relocationWidgetStore.updateMfaEnabled(true);
          wizardState.gotoStep("mfa");
        } else if (response.status === 400) {
          setLoginFormError("unauthenticated");
          throw new Error("The given credentials are invalid.");
        } else {
          setLoginFormError("unknownError");
          throw new Error("An unknown error occurred.");
        }
      } else {
        try {
          const data = await LegacySystemAPI.login(identifier, password);

          relocationWidgetStore.updateCustomerNumber(identifier);

          if (data.name === "TOTP") {
            relocationWidgetStore.updateMfaEnabled(true);
            wizardState.gotoStep("mfa");
          } else if (data.name === "DeviceCookie") {
            relocationWidgetStore.updateDeviceCookieNeeded(true);
            wizardState.gotoStep("deviceCookie");
          } else if (data.token) {
            relocationWidgetStore.updateAccessToken(data.token);
            relocationForm.setValue("userId", "");
            wizardState.gotoStep("target");
          } else if (data.msg === "invalid credentials") {
            setLoginFormError("unauthenticated");
          } else {
            setLoginFormError("unknownError");
          }
        } catch (e) {
          setLoginFormError("unauthenticated");
          throw e;
        }
      }
    },
  });
  const [watchedIdentifier, watchedPassword] = loginForm.watch([
    "identifier",
    "password",
  ]);

  const isLoggedIn = useSelector(() => !!relocationWidgetStore.accessToken);
  const mfaEnabled = useSelector(() => relocationWidgetStore.mfaEnabled);
  const deviceCookieNeeded = useSelector(
    () => relocationWidgetStore.deviceCookieNeeded,
  );

  useEffect(() => {
    if (!isNewSystem) {
      relocationForm.setValue("target.organisationUUID", watchedIdentifier);
    }
  }, [watchedIdentifier]);

  const loginStep = !isLoggedIn && (
    <LoginStep
      form={loginForm}
      formError={loginFormError}
      isNewSystem={isNewSystem}
    />
  );

  const mfaStep = (
    <MfaStep
      email={watchedIdentifier}
      isNewSystem={isNewSystem}
      password={watchedPassword}
      rejectionAnimation={rejectionAnimation}
      wizardState={wizardState}
      onSuccessfulLogin={(userId) => relocationForm.setValue("userId", userId)}
    />
  );

  const deviceCookieStep = (
    <DeviceCookieStep
      email={watchedIdentifier}
      isNewSystem={isNewSystem}
      password={watchedPassword}
      rejectionAnimation={rejectionAnimation}
      wizardState={wizardState}
    />
  );

  const resultScreen = success === true && (
    <ResultBox
      email={relocationForm.watch("contact.email")}
      system={relocationForm.watch("target.system")}
    />
  );

  const formScreen = success === undefined && (
    <ColumnLayout large={[2, 1]}>
      <ContentBox>
        <Wizard
          form={relocationForm}
          steps={[
            "tariff",
            "login",
            "mfa",
            "deviceCookie",
            "target",
            "details",
            "additionalServices",
            "contact",
            "overview",
          ]}
          wizardState={wizardState}
        >
          <TariffStep />
          {!isLoggedIn && loginStep}
          {!isLoggedIn && mfaEnabled && mfaStep}
          {!isLoggedIn && deviceCookieNeeded && deviceCookieStep}
          {isLoggedIn && <TargetStep noReturn referrer="widget" />}
          {isLoggedIn && <DetailsStep />}
          {isLoggedIn && <AdditionalServicesStep />}
          {isLoggedIn && <ContactStep />}
          {isLoggedIn && <OverviewStep referrer="widget" />}
        </Wizard>
      </ContentBox>
      <ContentBox>
        <OverviewBox form={relocationForm.watch()} referrer="widget" />
      </ContentBox>
    </ColumnLayout>
  );

  return (
    <React.Fragment>
      {resultScreen}
      {formScreen}
    </React.Fragment>
  );
});
